import { useEffect, useState } from 'react';
import { Logo } from '../brand/Logo';
import { Button } from '../ui/Button';
import { nav } from '../../content/site';
import { sanitizeHref } from '../../lib/url';

/**
 * Navbar sticky con menú móvil.
 *
 * El prototipo simplemente ocultaba los enlaces por debajo de 860px y dejaba
 * anotado "implementar menú hamburguesa en producción" — es decir, la
 * navegación era inalcanzable en móvil. Aquí está implementado, con lo que un
 * menú móvil necesita para ser usable con teclado y lector de pantalla:
 * `aria-expanded`, `aria-controls`, cierre con Escape y bloqueo del scroll de
 * fondo mientras está abierto.
 */
export function Navbar() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    // Impide que el fondo haga scroll detrás del panel abierto.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-60 border-b border-line-soft bg-white/86 backdrop-blur-[14px]">
      <div className="shell flex h-[78px] items-center justify-between gap-6">
        <a
          href="#inicio"
          className="text-[27px] leading-none"
          aria-label="Togados — inicio"
        >
          <Logo />
        </a>

        {/* Navegación de escritorio. */}
        <nav aria-label="Principal" className="hidden items-center gap-9 min-[861px]:flex">
          {nav.links.map((link) => (
            <a
              key={link.href}
              href={sanitizeHref(link.href)}
              className="text-[15px] text-ink transition-colors hover:text-brand"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/*
           * `max-[860px]:hidden` y no `hidden min-[861px]:inline-block`:
           * Button ya trae `inline-block` en sus clases base, y como ambas
           * utilidades tienen la misma especificidad decide el orden del CSS
           * emitido — donde las utilidades base van después de `hidden`. El
           * resultado era un botón de escritorio visible en móvil. Una utilidad
           * dentro de media query se emite después y sí gana.
           */}
          <Button href={nav.cta.href} className="max-[860px]:hidden">
            {nav.cta.label}
          </Button>

          {/* Disparador del menú móvil. */}
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-controls="menu-movil"
            aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
            className="flex h-11 w-11 flex-col items-center justify-center gap-[5px] rounded-btn border border-line text-ink min-[861px]:hidden"
          >
            <span
              className={`block h-0.5 w-5 bg-current transition-transform duration-250 ${
                open ? 'translate-y-[7px] rotate-45' : ''
              }`}
            />
            <span
              className={`block h-0.5 w-5 bg-current transition-opacity duration-250 ${
                open ? 'opacity-0' : ''
              }`}
            />
            <span
              className={`block h-0.5 w-5 bg-current transition-transform duration-250 ${
                open ? '-translate-y-[7px] -rotate-45' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {/*
       * El panel se desmonta al cerrarse en lugar de ocultarse con CSS: así sus
       * enlaces salen del orden de tabulación y del árbol de accesibilidad, en
       * vez de quedar como trampas de foco invisibles.
       */}
      {open && (
        <nav
          id="menu-movil"
          aria-label="Principal (móvil)"
          className="border-t border-line-soft bg-white px-7 pb-7 pt-2 min-[861px]:hidden"
        >
          <ul className="flex flex-col">
            {nav.links.map((link) => (
              <li key={link.href}>
                <a
                  href={sanitizeHref(link.href)}
                  onClick={() => setOpen(false)}
                  className="block border-b border-line-soft py-4 text-base text-ink transition-colors hover:text-brand"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          {/* El panel se cierra al pulsar: si el destino está pendiente, el
              aviso aparecería tapado por el menú abierto. */}
          <Button
            href={nav.cta.href}
            onActivate={() => setOpen(false)}
            className="mt-6 w-full text-center"
          >
            {nav.cta.label}
          </Button>
        </nav>
      )}
    </header>
  );
}
